import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  VStack,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';

import {
  useInstanceGraph,
  useMeshDocument,
  useNode,
} from '../../hooks/instanceGraphContext';
import { DataDictionaryTables } from '../dataDictionary/DataDictionary';
import { DownloadMenuButton } from './DownloadMenuButton';
import { getNodeErrors } from './utils';
import { Alert } from '~/packages/common-frontend/components/Alert';
import { DataTapeErrorView } from '~/packages/manualDataTapeUploads/ui/DataTapeErrorView';

// TODO(jon): should probably apply this globally.
const tabProps = {
  marginTop: 0,
  marginRight: 0,
  fontSize: 'sm',
  _selected: {
    color: 'blue.400',
    borderBottomColor: 'blue.400',
    borderBottomWidth: '4px',
    boxShadow: 'none',
  },
};

const NodeStatusAlert = ({
  nodeStatus,
}: {
  nodeStatus?: DbTable.ArtifactInstance.Status;
}) => {
  switch (nodeStatus) {
    case 'error':
      // We have dedicated error handling UI.
      return null;
    case undefined:
      return (
        <Alert status="info">
          <Text>Not started</Text>
        </Alert>
      );
    case 'initializing':
    case 'inProgress':
      return (
        <Alert status="info">
          <Text>In progress</Text>
        </Alert>
      );
    case 'success':
      return (
        <Alert status="success">
          <Text>Successfully completed</Text>
        </Alert>
      );
    default:
      return finleyAssertUnreachable(nodeStatus);
  }
};

type DrawerTabType = 'errors' | 'dataDictionary';

export const InstanceDrawer = ({
  artifactId,
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
  artifactId: string;
}) => {
  const node = useNode(artifactId);
  const { nodes } = useInstanceGraph();
  const { meshInputs } = useMeshDocument();

  const nodeStatus = node?.instance?.status;
  const errors = useMemo(() => getNodeErrors({ nodes, artifactId }), [
    artifactId,
    nodes,
  ]);
  const dataDictionary = useMemo(() => {
    const meshInput = meshInputs.find(
      (input) => input.artifactId === artifactId,
    );
    return meshInput?.type === 'file' ? meshInput.dataDictionary : [];
  }, [artifactId, meshInputs]);

  const availableTabs = useMemo<DrawerTabType[]>(() => {
    const tabs: DrawerTabType[] = [];
    if (errors.length > 0) {
      tabs.push('errors');
    }
    if (dataDictionary.length > 0) {
      tabs.push('dataDictionary');
    }
    return tabs;
  }, [errors.length, dataDictionary.length]);

  const [selectedTab, setSelectedTab] = useState<DrawerTabType | null>(null);
  useEffect(() => {
    setSelectedTab((prevSelectedTab) => {
      if (prevSelectedTab && availableTabs.includes(prevSelectedTab)) {
        return prevSelectedTab;
      }
      if (availableTabs.length > 0) {
        return availableTabs[0];
      }
      return null;
    });
  }, [availableTabs]);

  if (!node) {
    return null;
  }

  const artifactInstanceId = node.instance?.id || null;
  const isSynthetic = node.instance?.synthetic || false;
  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="xl" closeOnOverlayClick>
      <DrawerOverlay />
      <DrawerContent padding="1rem">
        <HStack justifyContent="space-between" alignItems="start" w="100%">
          <DrawerHeader flexGrow={1} maxWidth="90%">
            {node.artifact.externalDisplayName}
          </DrawerHeader>
          <Box p="16px">
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              onClick={onClose}
              size="sm"
              variant="ghost"
            />
          </Box>
        </HStack>
        <DrawerBody>
          <VStack spacing="20px" alignItems="flex-start">
            {artifactInstanceId && !isSynthetic && (
              <DownloadMenuButton
                buttonLabel="Download"
                artifactInstanceId={artifactInstanceId}
              />
            )}
            {/* NodeStatusAlert is a placeholder in cases where there are no tabs. */}
            {availableTabs.length === 0 && (
              <NodeStatusAlert nodeStatus={nodeStatus} />
            )}
            {selectedTab && availableTabs.includes(selectedTab) && (
              <Tabs width="100%" index={availableTabs.indexOf(selectedTab)}>
                <TabList>
                  {availableTabs.map((tab) => (
                    <Tab
                      {...tabProps}
                      key={tab}
                      onClick={() => setSelectedTab(tab)}
                    >
                      {commonUtils.toTitleCase(tab)}
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
            )}
            {/* TODO(jon): this sidebar isn't wide enough for errors */}
            {selectedTab === 'errors' && (
              <DataTapeErrorView
                errors={errors}
                numOfRows={100}
                collapsible={false}
              />
            )}
            {/* TODO(jon): this sidebar isn't wide enough for data dictionary */}
            {selectedTab === 'dataDictionary' && (
              <DataDictionaryTables dataDictionary={dataDictionary} />
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const InstanceOptionalDrawer = () => {
  const { selectedNodeId, setSelectedNode } = useInstanceGraph();
  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = () => {
    setIsOpen(false);
    setSelectedNode(null);
  };

  React.useEffect(() => {
    setIsOpen(!!selectedNodeId);
  }, [selectedNodeId]);

  if (!selectedNodeId) {
    return null;
  }
  return (
    <InstanceDrawer
      artifactId={selectedNodeId}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};
